// 組織管理頁與範圍（SPEC #vms-web-220「組織管理頁與範圍」，VMS UX 評審 2026-10-13 P0-3、B-04）：
//   - 組織管理類頁面一次只對一個組織，組織 = 頁首範圍所在的組織；頁面內不另放組織下拉（不再有會偷改頁首範圍的頁內選擇器）
//   - 頁標題下方一個膠囊「組織：EddyStudio ▾」，點了列出這頁能管的組織；選了 = 切頁首範圍到那個組織，並 toast「已把範圍切到 X」
//   - 範圍是「全部」或某種來源、而能管的組織不只一個 → 先問「要管理哪個組織？」（每個組織一張卡：名稱、你的角色、設備數）
//   - 只有一個能管的組織 → 直接用它
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useScope } from '../../lib/scope';
import type { MyOrg } from '../../lib/planeApi';

/** 目前要管的組織：範圍指到、而且在候選裡的那個；範圍沒指到時只有一個候選就用它，否則 null（要先問）。 */
export function gateOrg(org: MyOrg | null, candidates: MyOrg[]): MyOrg | null {
  if (org) {
    const hit = candidates.find((o) => o.org_id === org.org_id);
    if (hit) return hit;
  }
  return candidates.length === 1 ? candidates[0] : null;
}

/**
 * 組織管理頁該列哪些組織：team 組織照列；個人組織只在自己在裡面有家戶時才列（每個帳號都有個人組織，
 * 沒有家戶的個人組織沒有東西可管，列出來只會讓「要管理哪個組織？」多一張沒用的卡）。
 */
export function relevantOrgs<O extends Pick<MyOrg, 'org_id' | 'kind'>>(orgs: O[], homes: { org_id?: string | null }[]): O[] {
  return orgs.filter((o) => o.kind === 'team' || homes.some((h) => h.org_id === o.org_id));
}

/** 組織名稱（個人組織沒取名時用「我的個人組織」）。 */
export function orgName(t: (k: string) => string, o: Pick<MyOrg, 'display_name' | 'kind'>): string {
  return o.display_name || (o.kind === 'personal' ? t('scope.personal_org') : '');
}

/**
 * 包住組織管理頁的內容：決定要管哪個組織，畫膠囊或「要管理哪個組織？」。
 * children 是 render prop，拿到已決定的組織才畫；沒有任何候選時畫 empty。
 */
export function OrgGate({ candidates, empty, children }: { candidates: MyOrg[]; empty?: ReactNode; children(org: MyOrg): ReactNode }) {
  const { org } = useScope();
  const current = gateOrg(org, candidates);
  if (!candidates.length) return <>{empty ?? null}</>;
  if (!current) return <OrgChooser candidates={candidates} />;
  return (
    <>
      <OrgCapsule current={current} candidates={candidates} />
      {children(current)}
    </>
  );
}

function useSwitchOrg() {
  const { t } = useTranslation();
  const { setScope } = useScope();
  return (o: MyOrg) => setScope('org:' + o.org_id, '', { announce: t('scope.switched_to', { name: orgName(t, o) }) });
}

/** 頁標題下的唯讀膠囊「組織：X ▾」；只有一個候選時不給下拉。 */
export function OrgCapsule({ current, candidates }: { current: MyOrg; candidates: MyOrg[] }) {
  const { t } = useTranslation();
  const switchOrg = useSwitchOrg();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', off);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', off);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const many = candidates.length > 1;
  return (
    <div className="org-capsule-wrap" ref={ref}>
      <button
        type="button"
        className="org-capsule"
        data-testid="org-capsule"
        data-org={current.org_id}
        aria-haspopup={many ? 'listbox' : undefined}
        aria-expanded={many ? open : undefined}
        disabled={!many}
        onClick={() => setOpen((o) => !o)}
      >
        {t('orggate.capsule', { name: orgName(t, current) })}
        {many && <span aria-hidden> ▾</span>}
      </button>
      {open && (
        <ul className="org-capsule-menu" role="listbox" aria-label={t('orggate.pick_title')}>
          {candidates.map((o) => (
            <li key={o.org_id} role="option" aria-selected={o.org_id === current.org_id}>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  if (o.org_id !== current.org_id) switchOrg(o);
                }}
              >
                {orgName(t, o)}
                <span className="muted small"> · {t(`people.org_role_${o.role}`, { defaultValue: o.role })}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** 範圍是「全部」又有多個能管的組織：先問要管理哪個。 */
export function OrgChooser({ candidates }: { candidates: MyOrg[] }) {
  const { t } = useTranslation();
  const { data } = useScope();
  const switchOrg = useSwitchOrg();
  const devCount = (id: string) => data.devices.filter((d) => d.org_id === id).length;
  return (
    <section className="org-chooser" data-testid="org-chooser">
      <h2 className="section-title">{t('orggate.pick_title')}</h2>
      <p className="muted small">{t('orggate.pick_body')}</p>
      <div className="org-cards">
        {candidates.map((o) => (
          <button key={o.org_id} type="button" className="card org-card" data-org={o.org_id} onClick={() => switchOrg(o)}>
            <strong>{orgName(t, o)}</strong>
            <span className="muted small">{t('orggate.your_role', { role: t(`people.org_role_${o.role}`, { defaultValue: o.role }) })}</span>
            <span className="muted small">{t('orggate.devices', { count: devCount(o.org_id) })}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
